import React, { useEffect, useLayoutEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import LinearProgress from '@mui/material/LinearProgress';
import CustomNoRowsOverlay from '../../components/noRowsOverlay/customNoRowsOverlay';
import {
  TextField,
  Stack,
  ButtonGroup,
  Grow,
  Paper,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Autocomplete,
  Card,
} from '@mui/material';
import Divider from '@mui/material/Divider';
import { useNavigate } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { DataGrid } from '@mui/x-data-grid';
import Title from '../../components/Title.js';
import { getFilterDatePresets, getFromToFilterBasedOnPreset } from '../../utility/filtersDatePresets';
import {
  selectInvoices,
  selectFilters,
  selectOrdering,
  selectListDirtyFlag,
  doUpdateFilters,
  doUpdateOrdering,
  doUpdateError,
  doGetProformasAsync,
  doCopyProformaAsync,
  selectError,
  selectErrorMessage,
  doDeleteProformaAsync,
  doUpdateProformaLeftToPayAmountAsync,
  selectPagination,
  doSetPage,
  doDeleteProformasAsync,
} from './ProformasSlice';
import Switch from '@mui/material/Switch';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { createPdfExportObject, downloadPdf } from '../../engine/documentExporter';
import { queueEmail } from '../../engine/documentExporter';
import { createEmailExportObject } from '../../engine/documentExporter';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import bankersRounding from 'bankers-rounding';
import navigationPaths from '../../engine/navigationPaths';
import { doSetTabs } from '../toolbarNavigationMenu/toolbarNavigationMenuSlice';
import ToolbarNavigationMenuIds from '../toolbarNavigationMenu/toolbarNavigationMenuIds';
import { CustomTotalsComponent } from './FooterTotals';
import { getLabelForPaymentMethod } from '../../utility/paymentMethods';
import ToastMessage from '../../components/toastMessage';
import DashboardLayout from 'components/dashboardLayout/dashboardLayout';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDInput from 'components/MDInput';
import FilterListIcon from '@mui/icons-material/FilterList';
import DataGridHeader from 'components/dataGridHeader';
import DataGridCell from 'components/dataGridCell';
import DocumentStatus from 'components/documentStatus';
import DataGridActionsMenu from 'components/dataGridActionsMenu';
import { formatNumber } from 'accounting-js';
import { DeleteListItemConfirmationModal } from 'components/DeleteListItemConfirmationModal/DeleteListItemConfirmationModal';
import { DocumentGroupActionsMenu } from 'components/DocumentGroupActionsMenu/DocumentGroupActionsMenu';
import { MassActions } from '../../components/ui';
import { AddDocumentButton } from 'components/AddDocumentButton/AddDocumentButton';

export function Proformas() {
  const FLOAT_PRECISION = 2;

  const invoices = useSelector(selectInvoices);
  const pagination = useSelector(selectPagination);
  const filters = useSelector(selectFilters);
  const ordering = useSelector(selectOrdering);
  const listDirty = useSelector(selectListDirtyFlag);

  const error = useSelector(selectError);
  const errorMessage = useSelector(selectErrorMessage);

  const [state, updateState] = useImmer({
    filtersOpened: false,
    orderingOpened: false,
    settingsOpened: false,
    filters: {
      preset: { value: 'thisMonth', label: 'aktualny miesiąc' },
    },
    customisationPanes: [],
    ordering: {
      seriesChecked: ordering.includes('series'),
      numberChecked: ordering.includes('number'),
      netValueChecked: ordering.includes('netValue'),
      grossValueChecked: ordering.includes('grossValue'),
      buyerNameChecked: ordering.includes('buyerName'),
      dateOfIssueUtcChecked: ordering.includes('dateOfIssueUtc'),
      dateOfSaleUtcChecked: ordering.includes('dateOfSaleUtc'),
    },
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  dayjs.extend(utc);
  dayjs.extend(timezone);

  useLayoutEffect(() => {
    return () => {
      dispatch(doSetPage(1));
    };
  }, []);

  React.useEffect(() => {
    dispatch(
      doSetTabs({
        mainTabId: ToolbarNavigationMenuIds().RootDocumentsTab,
        subTabId: ToolbarNavigationMenuIds().DocumentsListInvoicesSubTab,
      }),
    );
  }, []);

  React.useEffect(() => {
    dispatch(doGetProformasAsync({ filters, pagination }));
  }, [filters, ordering, pagination]);

  React.useEffect(() => {
    if (listDirty) {
      dispatch(doGetProformasAsync({ filters, pagination }));
    }
  }, [listDirty]);

  const buildPayload = (id, value) => {
    return {
      type: id,
      value: value,
    };
  };

  const [toastState, setUserIndication] = React.useState({
    visible: false,
    text: '',
  });

  const onAddNewInvoiceHandler = (e) => {
    navigate(navigationPaths().ProformasForm, { state: { mode: 'create' } });
  };

  const onDeleteSelectedHandler = (e) => {
    handleDeleteInvoiceClick(e);
    const selectedIds = e.selectionModel;
    if (selectedIds.length > 0) {
      dispatch(doDeleteProformasAsync(selectedIds));
    }
  };

  const onCustomisationPanesChanged = (event, newPanes) => {
    updateState((draft) => {
      draft.customisationPanes = newPanes;

      draft.filtersOpened = newPanes.includes('filters');
      draft.orderingOpened = newPanes.includes('ordering');
      draft.settingsOpened = newPanes.includes('settings');
    });
  };

  // #region Filters

  function filtersPane() {
    return (
      <Card sx={{ my: 1, py: 1 }} elevation={4}>
        <Stack direction="column" sx={{ pl: 2, pb: 2 }}>
          <Title>Filtry</Title>
          <Divider />
          <Stack sx={{ pt: 2 }} direction="row" spacing={2}>
            <Autocomplete
              disablePortal
              size="small"
              variant="outlined"
              sx={{ width: 200 }}
              disableClearable
              value={state.filters.preset}
              isOptionEqualToValue={onIsOptionEqualToValue}
              onChange={(event, value) => onFilterPresetChanged(event, value)}
              options={getFilterDatePresets()}
              renderInput={(params) => <MDInput variant="outlined" {...params} label="Okres" />}
            />
            <Divider orientation="vertical" flexItem />
            <DesktopDatePicker
              label="Od"
              InputProps={{ sx: { width: 150 } }}
              value={filters.fromDate}
              onChange={onFromDateChanged}
              renderInput={(params) => <TextField variant="outlined" required size="small" {...params} />}
            />
            <DesktopDatePicker
              size="small"
              label="Do"
              InputProps={{ sx: { width: 150 } }}
              value={filters.toDate}
              onChange={onToDateChanged}
              renderInput={(params) => <TextField variant="outlined" required size="small" {...params} />}
            />
          </Stack>
        </Stack>
      </Card>
    );
  }

  const onFilterPresetChanged = (event, newPreset) => {
    updateState((draft) => {
      draft.filters.preset = newPreset;
    });

    const filters = getFromToFilterBasedOnPreset(newPreset);
    dispatch(doUpdateFilters(buildPayload('filters', filters)));
  };

  const onIsOptionEqualToValue = (option, value) => {
    return option.id === value;
  };

  const onFromDateChanged = (value) => {
    dispatch(doUpdateFilters(buildPayload('fromDate', value)));

    updateState((draft) => {
      draft.filters.preset = { value: 'custom', label: 'dowolny' };
    });
  };

  const onToDateChanged = (value) => {
    dispatch(doUpdateFilters(buildPayload('toDate', value)));

    updateState((draft) => {
      draft.filters.preset = { value: 'custom', label: 'dowolny' };
    });
  };

  // #endregion

  // #region Ordering

  function orderPane() {
    return (
      <Card sx={{ my: 1, py: 1 }} elevation={4}>
        <Stack direction="column" sx={{ pl: 2, pb: 2 }}>
          <Title>Sortowanie</Title>
          <Divider />
          <Stack sx={{ pt: 2 }} direction="column" justifyContent="flex-start" alignItems="flex-start">
            <FormControlLabel
              control={<Switch checked={state.ordering.seriesChecked} onChange={onOrderingChange} name="series" />}
              label="Seria"
              labelPlacement="start"
              size="small"
            />
            <FormControlLabel
              control={<Switch checked={state.ordering.numberChecked} onChange={onOrderingChange} name="number" />}
              label="Numer"
              labelPlacement="start"
              size="small"
            />
            <FormControlLabel
              control={<Switch checked={state.ordering.netValueChecked} onChange={onOrderingChange} name="netValue" />}
              label="Wartość netto"
              labelPlacement="start"
              size="small"
            />
            <FormControlLabel
              control={
                <Switch checked={state.ordering.grossValueChecked} onChange={onOrderingChange} name="grossValue" />
              }
              label="Wartość brutto"
              labelPlacement="start"
              size="small"
            />
            <FormControlLabel
              control={
                <Switch checked={state.ordering.buyerNameChecked} onChange={onOrderingChange} name="buyerName" />
              }
              label="Nabywca"
              labelPlacement="start"
              size="small"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={state.ordering.dateOfIssueUtcChecked}
                  onChange={onOrderingChange}
                  name="dateOfIssueUtc"
                />
              }
              label="Data wystawienia"
              labelPlacement="start"
              size="small"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={state.ordering.dateOfSaleUtcChecked}
                  onChange={onOrderingChange}
                  name="dateOfSaleUtc"
                />
              }
              label="Data sprzedaży"
              labelPlacement="start"
              size="small"
            />
          </Stack>
        </Stack>
      </Card>
    );
  }

  const onOrderingChange = (event) => {
    const newOrdering = [];

    updateState((draft) => {
      switch (event.target.name) {
        case 'series':
          draft.ordering.seriesChecked = !draft.ordering.seriesChecked;
          break;
        case 'number':
          draft.ordering.numberChecked = !draft.ordering.numberChecked;
          break;
        case 'netValue':
          draft.ordering.netValueChecked = !draft.ordering.netValueChecked;
          break;
        case 'grossValue':
          draft.ordering.grossValueChecked = !draft.ordering.grossValueChecked;
          break;
        case 'buyerName':
          draft.ordering.buyerNameChecked = !draft.ordering.buyerNameChecked;
          break;
        case 'dateOfIssueUtc':
          draft.ordering.dateOfIssueUtcChecked = !draft.ordering.dateOfIssueUtcChecked;
          break;
        case 'dateOfSaleUtc':
          draft.ordering.dateOfSaleUtcChecked = !draft.ordering.dateOfSaleUtcChecked;
          break;
      }

      if (draft.ordering.seriesChecked) newOrdering.push('series');
      if (draft.ordering.numberChecked) newOrdering.push('number');
      if (draft.ordering.netValueChecked) newOrdering.push('netValue');
      if (draft.ordering.grossValueChecked) newOrdering.push('grossValue');
      if (draft.ordering.buyerNameChecked) newOrdering.push('buyerName');
      if (draft.ordering.dateOfIssueUtcChecked) newOrdering.push('dateOfIssueUtc');
      if (draft.ordering.dateOfSaleUtcChecked) newOrdering.push('dateOfSaleUtc');
    });

    dispatch(doUpdateOrdering(newOrdering));
  };

  // #endregion

  // #region Settings

  function settingsPane() {
    return (
      <Paper sx={{ my: 1, py: 1 }} elevation={4}>
        <Stack direction="column" sx={{ pl: 2, pb: 2 }}>
          <Typography sx={{ pb: 1 }} fontSize={24}>
            Ustawienia
          </Typography>
          <Divider />
          <Stack sx={{ pt: 2 }} direction="row" spacing={2}></Stack>
        </Stack>
      </Paper>
    );
  }

  // #endregion

  // #region Totals

  const [totalAmount, setTotalAmount] = React.useState(0);
  const [totalAmountNet, setTotalAmountNet] = React.useState(0);
  const [selectedInvoices, setSelectedInvoices] = React.useState([]);
  React.useEffect(() => {
    onRowSelectionChange(selectedInvoices);
  }, [selectedInvoices, invoices]);
  const onRowSelectionChange = (ids) => {
    const selected = new Set(ids);
    const selectedInvoiceData = invoices.filter((row) => selected.has(row.id.toString()));
    var totalAmountCount = 0;
    var totalAmountNetCount = 0;
    if (selectedInvoiceData.length == 0) {
      // empty selection, count all invoices
      invoices.forEach((el) => {
        totalAmountCount = totalAmountCount + el.amountToPay;
        totalAmountNetCount = totalAmountNetCount + el.amountToPayNet;
      });
    } else {
      // some invoices selected, count only the selected once
      selectedInvoiceData.forEach((el) => {
        console.log(el);
        totalAmountCount = totalAmountCount + el.amountToPay;
        totalAmountNetCount = totalAmountNetCount + el.amountToPayNet;
      });
    }
    setTotalAmount(totalAmountCount);
    setTotalAmountNet(totalAmountNetCount);
  };
  // Make totals pretty
  React.useEffect(() => {}, []);
  // #endregion

  // #region columns functions

  function getItemIndex(data) {
    return data.api.getRowIndex(data.row.id) + data.api.state.pagination.page * data.api.state.pagination.pageSize + 1;
  }

  function getInvoiceNumber(data) {
    return data.row.number + '/' + data.row.series;
  }

  function getFormattedAmountToPayValue(data) {
    return onFormatWithPrecision(data.row.amountToPay) + ' ' + data.row.currencyCode;
  }

  function getFormattedAmountToPayNetValue(data) {
    return onFormatWithPrecision(data.row.amountToPayNet) + ' ' + data.row.currencyCode;
  }

  function getInvoiceNetValue(data) {
    return bankersRounding(
      data.row.items.reduce((a, v) => a + v.amountNet, 0),
      FLOAT_PRECISION,
    );
  }

  function getInvoiceGrossValue(data) {
    return bankersRounding(
      data.row.items.reduce((a, v) => a + v.amountGross, 0),
      FLOAT_PRECISION,
    );
  }

  function onFormatWithPrecision(value) {
    if (value == null) {
      return '0,0';
    }
    return formatNumber(value, { precision: FLOAT_PRECISION, thousand: ' ', decimal: ',' });
  }

  function getInvoiceBuyerName(data) {
    return data.row.buyerName;
  }

  function getInvoicePaymentName(data) {
    const paymentLabel = getLabelForPaymentMethod(data.row.paymentCode);
    return paymentLabel;
  }

  function formatInvoiceDate(cell) {
    return dayjs(cell.value).format('DD-MM-YYYY');
  }

  const handlePreviewClick = (e) => {
    navigate(navigationPaths().ProformasPreview, { state: { id: e } });
  };

  const handleEditClick = (e) => {
    navigate(navigationPaths().ProformasForm, { state: { mode: 'edit', id: e } });
  };

  const handleCopyDocumentClick = (e) => {
    dispatch(doCopyProformaAsync({ id: e, invoiceDateUtc: dayjs(), timezone: dayjs.tz.guess() }));
  };

  const handleRepeatDocumentClick = (e) => {
    navigate(navigationPaths().ProformasForm, { state: { mode: 'editPreview', id: e } });
  };

  const handleCreateInvoiceClick = (e) => {
    navigate(navigationPaths().InvoicesForm, { state: { mode: 'editPreviewFromProforma', id: e } });
  };

  const handleMarkAsPaidClick = (e) => {
    dispatch(doUpdateProformaLeftToPayAmountAsync({ id: e, leftToPay: 0 }));
  };

  const handleMarkAsUnpaidClick = (e) => {
    const invoice = invoices.find((invoice) => invoice.id === e);
    dispatch(doUpdateProformaLeftToPayAmountAsync({ id: e, leftToPay: invoice?.amountToPay }));
  };

  const handleExportToPdfClick = async (id, template) => {
    var payload = createPdfExportObject(template);
    payload.documentId = id;

    await downloadPdf(payload);
  };
  const handleSendEmailClick = (id) => async () => {
    var payload = createEmailExportObject();
    payload.documentId = id;
    await queueEmail(payload);

    setUserIndication({
      visible: true,
      text: 'Wysłano dokument!',
    });
  };

  const handleToastClose = () => {
    setUserIndication({
      visible: false,
      text: '',
    });
  };

  const isDocumentPaidFor = (id) => {
    const invoice = invoices.find((invoice) => invoice.id === id);
    return invoice?.leftToPay === 0;
  };

  // #endregion

  // #region Invoice deleting

  const [deleteDialogConfirmationOpen, setDeleteDialogConfirmationOpen] = React.useState(false);
  const [deleteInvoiceGuids, setDeleteInvoiceGuids] = React.useState([]);
  const handleDeleteInvoiceClick = (id) => {
    setDeleteDialogConfirmationOpen(true);
    setDeleteInvoiceGuids([id]);
  };

  const handleDeleteSelectedInvoicesClick = () => {
    setDeleteDialogConfirmationOpen(true);
    setDeleteInvoiceGuids(selectedInvoices);
  };

  const deleteSingleDocument = (id) => {
    dispatch(doDeleteProformaAsync(id));
  };

  const deleteMultipleDocuments = (ids) => {
    dispatch(doDeleteProformasAsync(ids));
  };

  const handleDeleteInvoiceAgreed = () => {
    if (deleteInvoiceGuids.length === 1) {
      deleteSingleDocument(deleteInvoiceGuids[0]);
    } else if (deleteInvoiceGuids.length > 1) {
      deleteMultipleDocuments(deleteInvoiceGuids);
    }
    setDeleteInvoiceGuids([]);
    setDeleteDialogConfirmationOpen(false);
  };

  const handleDeleteInvoiceDisagreed = () => {
    setDeleteInvoiceGuids([]);
    setDeleteDialogConfirmationOpen(false);
  };

  const renderColHeader = (params) => <DataGridHeader>{params.colDef.headerName}</DataGridHeader>;
  // #endregion

  // #region columns definitions

  const invoiceColumns = [
    {
      field: 'index',
      headerName: 'ID',
      filterable: false,
      sortable: false,
      width: 72,
      headerAlign: 'left',
      valueGetter: getItemIndex,
      renderHeader: renderColHeader,
      renderCell: (params) => <DataGridCell>{params.value}</DataGridCell>,
    },
    {
      field: 'number',
      headerName: 'Numer',
      width: 180,
      filterable: false,
      sortable: true,
      headerAlign: 'left',
      type: 'string',
      valueGetter: getInvoiceNumber,
      renderHeader: renderColHeader,
      renderCell: (params) => (
        <DataGridCell main rowId={params.row.id} itemClicked={handlePreviewClick}>
          {params.value}
        </DataGridCell>
      ),
    },
    {
      field: 'buyerName',
      headerName: 'Klient',
      flex: 2,
      width: 249,
      filterable: false,
      sortable: true,
      headerAlign: 'left',
      type: 'string',
      valueGetter: getInvoiceBuyerName,
      renderHeader: renderColHeader,
      renderCell: (params) => <DataGridCell>{params.value}</DataGridCell>,
    },
    {
      field: 'dateOfIssueUtc',
      headerName: 'Data wystawienia',
      width: 184,
      filterable: false,
      headerAlign: 'left',
      sortable: true,
      type: 'string',
      valueFormatter: formatInvoiceDate,
      renderHeader: renderColHeader,
      renderCell: (params) => <DataGridCell>{params.formattedValue}</DataGridCell>,
    },
    {
      field: 'amountToPayNet',
      headerName: 'Wartość netto',
      width: 160,
      filterable: false,
      sortable: true,
      headerAlign: 'left',
      type: 'string',
      valueGetter: getFormattedAmountToPayNetValue,
      renderHeader: renderColHeader,
      renderCell: (params) => <DataGridCell>{params.formattedValue}</DataGridCell>,
    },
    {
      field: 'amountToPay',
      headerName: 'Wartość brutto',
      width: 160,
      filterable: false,
      sortable: true,
      headerAlign: 'left',
      type: 'string',
      valueGetter: getFormattedAmountToPayValue,
      renderHeader: renderColHeader,
      renderCell: (params) => <DataGridCell>{params.formattedValue}</DataGridCell>,
    },
    {
      field: 'paymentCode',
      headerName: 'Płatność',
      width: 123,
      filterable: false,
      sortable: true,
      headerAlign: 'left',
      type: 'string',
      valueGetter: getInvoicePaymentName,
      renderHeader: renderColHeader,
      renderCell: (params) => <DataGridCell>{params.formattedValue}</DataGridCell>,
    },
    {
      field: 'statuses',
      headerName: 'Status',
      width: 150,
      filterable: false,
      sortable: true,
      headerAlign: 'left',
      renderHeader: renderColHeader,
      renderCell: (params) => (params.row.leftToPay === 0 ? <DocumentStatus paid /> : <DocumentStatus />),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Akcje',
      width: 120,
      cellClassName: 'actions',
      headerAlign: 'right',
      renderHeader: renderColHeader,
      renderCell: (params) => (
        <DataGridActionsMenu
          invoice
          reinvoice
          preview
          pdf
          duplicatePdf
          payment
          remove
          rowId={params.row.id}
          isPaid={params.row.leftToPay == 0}
          handleMenuOptionClick={handleMenuOptionClick}
        />
      ),
    },
  ];

  const handleMenuOptionClick = (optionId, rowId) => {
    switch (optionId) {
      case 'preview':
        handlePreviewClick(rowId);
        break;
      case 'pdf':
        handleExportToPdfClick(rowId, 'proforma');
        break;
      case 'duplicatePdf':
        handleExportToPdfClick(rowId, 'proformaDuplicate');
        break;        
      case 'payment':
        if (isDocumentPaidFor(rowId)) {
          handleMarkAsUnpaidClick(rowId);
        } else {
          handleMarkAsPaidClick(rowId);
        }
        break;
      case 'reinvoice':
        handleRepeatDocumentClick(rowId);
        break;
      case 'delete':
        handleDeleteInvoiceClick(rowId);
        break;
      case 'edit':
        handleEditClick(rowId);
        break;
      case 'invoice':
        handleCreateInvoiceClick(rowId);
        break;
      default:
        break;
    }
  };
  // #endregion

  const setOpenInvoiceErrorToast = (value) => {
    dispatch(doUpdateError(value));
  };

  return (
    <DashboardLayout>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DashboardNavbar />
        <MDBox>
          <ToastMessage
            open={error}
            onClose={() => setOpenInvoiceErrorToast(false)}
            severity="error"
            message={errorMessage}
          />
          {deleteDialogConfirmationOpen && (
            <DeleteListItemConfirmationModal
              id="delete-confirmation-dialog"
              onConfirm={handleDeleteInvoiceAgreed}
              onClose={handleDeleteInvoiceDisagreed}
              title={selectedInvoices?.length > 1 ? 'Usunąć faktury?' : 'Usunąć fakturę?'}
              confirmText={selectedInvoices?.length > 1 ? 'Tak, usuń faktury' : 'Tak, usuń fakturę'}
              message={
                selectedInvoices?.length > 1
                  ? 'Czy na pewno chcesz usunąć faktury? Tej operacji nie będzie można cofnąć.'
                  : 'Czy na pewno chcesz usunąć fakturę? Tej operacji nie będzie można cofnąć.'
              }
            />
          )}
          <Stack direction="column">
            <Stack sx={{ py: 2 }} direction="row" justifyContent="space-between">
              <ButtonGroup size="small">
                <AddDocumentButton id="addInvoice" onClick={onAddNewInvoiceHandler}>
                  Dodaj proformę
                </AddDocumentButton>
              </ButtonGroup>
              <Stack>
                <Grow in={state.orderingOpened}>{state.orderingOpened ? orderPane() : <div></div>}</Grow>
                <Grow in={state.settingsOpened}>{state.settingsOpened ? settingsPane() : <div></div>}</Grow>
              </Stack>
            </Stack>
            <Card sx={{ px: 0, py: 0, my: 2 }}>
              <Stack id="invoices-list">
                <Stack direction="row" justifyContent="flex-start" alignItems="center" sx={{ px: 2, py: 1 }}>
                  <Typography
                    sx={{ color: '#344767', fontFamily: 'Roboto, Helvetica', fontWeight: 700, fontSize: '18px' }}
                  >
                    Faktury
                  </Typography>
                  <Stack sx={{ ml: 1 }}>
                    <ToggleButtonGroup
                      color="info"
                      size="small"
                      value={state.customisationPanes}
                      onChange={onCustomisationPanesChanged}
                    >
                      <ToggleButton color="primary" sx={{ textTransform: 'none', border: 'none' }} value="filters">
                        <FilterListIcon fontSize="medium" />
                        Filtry
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                </Stack>
                {state.filtersOpened ? filtersPane() : <div></div>}

                {selectedInvoices.length > 0 && (
                  <MassActions
                    items={[
                      {
                        key: 'deleteSelected',
                        label: 'Usuń zaznaczone',
                        onClick: handleDeleteSelectedInvoicesClick,
                      },
                    ]}
                  />
                )}

                <DataGrid
                  sx={{ '& > .MuiDataGrid-columnSeparator': { visibility: 'hidden' }, border: 'none' }}
                  name="invoices"
                  fontSize={13}
                  spacing={10}
                  autoHeight={true}
                  autoPageSize={false}
                  rows={invoices}
                  rowHeight={64}
                  loading={listDirty}
                  columns={invoiceColumns}
                  disableSelectionOnClick
                  disableColumnFilter
                  disableColumnMenu
                  checkboxSelection
                  pagination
                  paginationMode="server"
                  rowCount={pagination.totalInvoices}
                  rowsPerPageOptions={pagination.rowsPerPageOptions}
                  page={pagination.page - 1}
                  pageSize={pagination.pageSize}
                  onPageChange={(data) => {
                    dispatch(doSetPage(data + 1));
                  }}
                  experimentalFeatures={{ newEditingApi: true }}
                  initialState={{
                    sorting: {
                      sortModel: [{ field: 'series', sort: 'asc' }],
                    },
                  }}
                  components={{
                    LoadingOverlay: LinearProgress,
                    NoRowsOverlay: CustomNoRowsOverlay,
                    Footer: CustomTotalsComponent,
                  }}
                  componentsProps={{ footer: { totalAmount, totalAmountNet } }}
                  onSelectionModelChange={(ids) => setSelectedInvoices(ids)}
                />
              </Stack>
            </Card>
          </Stack>
          <Snackbar onClose={handleToastClose} open={toastState.visible} autoHideDuration={3000}>
            <Alert severity="success" sx={{ width: '100%' }}>
              {toastState.text}
            </Alert>
          </Snackbar>
        </MDBox>
      </LocalizationProvider>
    </DashboardLayout>
  );
}
